// vg-binding: P7.D-111
// vg-binding: P7.D-112
// vg-binding: P7.D-115
// vg-binding: P7.D-116
//
// apps/vendor/src/features/dashboard/GettingStartedChecklist.tsx
//
// Phase 7 Wave 4 Task 13 — post-KYC-approval getting-started widget.
//
// Renders ONLY when `kyc_approved=true AND checklist_dismissed=false`
// (gate enforced by the caller — Dashboard.tsx — so the widget itself
// stays UI-only). 4 steps: Complete profile / Add product / Configure
// carrier / Complete test order. Progress bar = completed/total %.

import { csrfFetch } from '../../lib/api/csrfFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn, useToast } from '@printway/ui';

// ---------------------------------------------------------------------------
// API types
// ---------------------------------------------------------------------------

interface ChecklistStep {
  id: 'complete_profile' | 'add_product' | 'configure_carrier' | 'test_order';
  label: string;
  completed: boolean;
  link: string;
  tips: string;
}

interface ChecklistData {
  steps: ChecklistStep[];
  dismissed: boolean;
  progress_percent: number;
}

interface ApiOk<T> {
  ok: true;
  data: T;
}
interface ApiErr {
  ok: false;
  error: { code: string; message: string };
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) throw new Error(body.error.message);
  return body.data;
}

const CHECKLIST_KEY = ['vendor', 'onboarding', 'checklist'] as const;

function useChecklist() {
  return useQuery<ChecklistData, Error>({
    queryKey: CHECKLIST_KEY,
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/onboarding/checklist', {
        method: 'GET',
        credentials: 'include',
      });
      return unwrap<ChecklistData>(res);
    },
  });
}

function useDismissChecklist() {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const qc = useQueryClient();
  return useMutation<{ dismissed: true }, Error, void>({
    mutationFn: async () => {
      const res = await csrfFetch(
        '/api/v1/vendor/onboarding/checklist/dismiss',
        {
          method: 'PATCH',
          credentials: 'include',
        },
      );
      return unwrap<{ dismissed: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHECKLIST_KEY });
    },
    // fix/vendor-batch-e — surface dismiss failures (silent-mutation sweep):
    // the widget previously just stayed on screen with no explanation.
    onError: (e) => {
      toast({
        variant: 'error',
        title: t('checklist.dismissError'),
        description: e.message,
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

interface Props {
  /** Render gate — Dashboard passes profile.kyc_approved. */
  enabled: boolean;
}

function CheckIcon(completed: boolean): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs',
        completed
          ? 'border-green-600 bg-green-50 text-green-700'
          : 'border-pw-border bg-pw-bg text-pw-text-muted',
      )}
    >
      {completed ? '✓' : ''}
    </span>
  );
}

function StepTestId(id: ChecklistStep['id']): string {
  switch (id) {
    case 'complete_profile':
      return 'checklist-step-complete-profile';
    case 'add_product':
      return 'checklist-step-add-product';
    case 'configure_carrier':
      return 'checklist-step-config-carrier';
    case 'test_order':
      return 'checklist-step-complete-test-order';
  }
}

export function GettingStartedChecklist({ enabled }: Props): JSX.Element | null {
  const { t } = useTranslation('dashboard');
  const checklistQ = useChecklist();
  const dismissM = useDismissChecklist();

  if (!enabled) return null;
  if (checklistQ.isLoading) {
    return (
      <section className="h-44 animate-pulse rounded-xl border border-pw-border bg-pw-surface" />
    );
  }
  if (!checklistQ.data || checklistQ.data.dismissed) return null;

  const { steps, progress_percent } = checklistQ.data;

  return (
    <section
      data-testid="dashboard-getting-started-checklist"
      className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-pw-text">
          {t('checklist.title')}
        </h3>
        <button
          type="button"
          data-testid="checklist-dismiss-btn"
          onClick={() => dismissM.mutate()}
          disabled={dismissM.isPending}
          className="text-xs font-medium text-pw-text-muted hover:text-pw-text"
        >
          {t('checklist.dismiss')}
        </button>
      </div>

      <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-pw-surface">
        <div
          className="h-full bg-pw-primary transition-all"
          style={{ width: `${progress_percent}%` }}
        />
      </div>
      <p className="mb-3 text-xs text-pw-text-muted">
        {t('checklist.progress', {
          percent: progress_percent,
          done: steps.filter((s) => s.completed).length,
          total: steps.length,
        })}
      </p>

      <ul className="flex flex-col gap-2">
        {steps.map((step) => (
          <li key={step.id}>
            <Link
              to={step.link}
              data-testid={StepTestId(step.id)}
              title={step.tips}
              className={cn(
                'flex items-center gap-3 rounded-md border border-pw-border bg-pw-surface px-3 py-2 transition-colors hover:bg-pw-bg',
                step.completed && 'opacity-75',
              )}
            >
              {CheckIcon(step.completed)}
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'text-sm font-medium',
                    step.completed
                      ? 'text-pw-text-muted line-through'
                      : 'text-pw-text',
                  )}
                >
                  {t(`checklist.steps.${step.id}.label`, {
                    defaultValue: step.label,
                  })}
                </p>
                <p className="truncate text-xs text-pw-text-muted">
                  {t(`checklist.steps.${step.id}.tips`, {
                    defaultValue: step.tips,
                  })}
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default GettingStartedChecklist;
